import { Component, Input, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChartConfiguration } from 'chart.js';
import { Area, Test } from 'src/app/models/test.model';
import { UtilsService } from 'src/app/services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import * as html2pdf from 'html2pdf.js';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
  selector: 'ct-digital-report',
  templateUrl: './digital-report.component.html',
  styleUrls: ['./digital-report.component.scss']
})
export class DigitalReportComponent implements OnInit {

  @Input() test: Test;

  public currentDate = new Date();
  public data: number[];
  public times: string[];
  public reportType: 'candidate' | 'consultant' | 'client';
  
  public medals = {
    1: 'bronze',
    2: 'silver',
    3: 'gold',
    4: 'diamond'
  };

  private computedBodyStyle = getComputedStyle(document.body)

  public colors = {
    primaryColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
    secondaryColor: this.computedBodyStyle.getPropertyValue('--color-secondary'),
    tertiaryColor: this.computedBodyStyle.getPropertyValue('--color-tertiary'),
    quaternaryColor: this.computedBodyStyle.getPropertyValue('--color-quaternary'),
    quinaryColor: this.computedBodyStyle.getPropertyValue('--color-quinary'),
    senaryColor: this.computedBodyStyle.getPropertyValue('--color-senary'),
    septenaryColor: this.computedBodyStyle.getPropertyValue('--color-septenary'),
    octonaryColor: this.computedBodyStyle.getPropertyValue('--color-octonary'),
    nonaryColor: this.computedBodyStyle.getPropertyValue('--color-nonary')
  }

  public language: string;
  private languageId: number;
  public areaResultExplanation: string;

  constructor(
    public renderer: Renderer2,
    private utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.snapshot.queryParamMap.get('languageId')
      ? this.languageId = Number(this.activatedRoute.snapshot.queryParamMap.get('languageId'))
      : this.languageId = Number(this.test.reportLanguageId);
    this.language = this.utilsService.getLanguageFromLanguageId(this.languageId);
    this.translateService.use(this.language);
    this.translateService.setDefaultLang(this.language)
    document.documentElement.lang = this.language.split('-')[0];

    // En el caso de tenerlo seteamos los valores del proyecto
    if (this.test.projectSelected && this.test.projectSelected.projectId) {

      this.colors = {
        primaryColor: this.test.projectSelected.projectPrimaryColor,
        secondaryColor: this.test.projectSelected.projectSecondaryColor,
        tertiaryColor: this.test.projectSelected.projectTertiaryColor,
        quaternaryColor: this.test.projectSelected.projectQuaternaryColor,
        quinaryColor: this.test.projectSelected.projectQuinaryColor,
        senaryColor: this.test.projectSelected.projectSenaryColor,
        septenaryColor: this.test.projectSelected.projectSeptenaryColor,
        octonaryColor: this.test.projectSelected.projectOctonaryColor,
        nonaryColor: this.test.projectSelected.projectNonaryColor
      }
      document.getElementById('reportModule').style.setProperty('--color-primary', this.colors.primaryColor);
      document.getElementById('reportModule').style.setProperty('--color-secondary', this.colors.secondaryColor);
      document.getElementById('reportModule').style.setProperty('--color-tertiary', this.colors.tertiaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quaternary', this.colors.quaternaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quinary', this.colors.quinaryColor);
      document.getElementById('reportModule').style.setProperty('--color-senary', this.colors.senaryColor);
      document.getElementById('reportModule').style.setProperty('--color-septenary', this.colors.septenaryColor);
      document.getElementById('reportModule').style.setProperty('--color-octonary', this.colors.octonaryColor);
      document.getElementById('reportModule').style.setProperty('--color-nonary', this.colors.nonaryColor);
    }

    switch (this.test.reportTypeId) {
      case '1': this.reportType = 'candidate'; break;
      case '2': this.reportType = 'consultant'; break;
      case '3': this.reportType = 'client'; break;
    }

  }

  getAreaScore(id: number): number {    
    const area = this.test.areas.find((area: Area) => area.questionGroupId === id);
    if (!area || area.score == null) {
      return undefined;
    }
    return Math.round(area.score / 10) * 10;
  }

  get sortedAreas() {
      return this.test?.areas
        ? this.test.areas.slice().sort((a, b) => (a.questionGroupId || 0) - (b.questionGroupId || 0))
        : [];
    }

  public level(score: number): string {
    if (score < 40) {
      return 'BASIC';
    } else if (score >= 40 && score < 65) {
      return 'INTERMEDIATE';
    } else if (score >= 65 && score < 85) {
      return 'ADVANCED';
    } else {
      return 'EXPERT';
    }
  }

  public levelByArea(area: string): string {
    const score = this.test.areas.find(a => a.description === area)?.score || 0;
    return this.level(score);
  }

  maxLevelApproved() {
    const index = this.test.levels.slice(-1)[0].levelDetail.cutOffMark < this.test.levels.slice(-1)[0].score ? -1 : -2;
    return this.test.levels.slice(index)[0].levelDetail.description;
  }

  print() {
    this.loadingService.startLoading();

    window.scrollTo(0, 0);
    this.renderer.setStyle(document.querySelector('body'), 'overflow', 'hidden');
    document.querySelectorAll('#digitalReport').forEach(element => this.renderer.setAttribute(element, 'class', 'print'));
    const fileName = this.utilsService.reportFileName(this.test, '', this.reportType);


    return new Promise<void>(resolve => setTimeout(() => {
      let worker = html2pdf().from(document.querySelectorAll('#digitalReport')[0]).set({
        pagebreak: { before: '.page, .break', avoid: 'p' },
        html2canvas: { scale: 2 },
        image: { type: 'jpeg', quality: 1 },
        filename: fileName
      }).toPdf();

      const pages = [...Array.from(document.querySelectorAll('#digitalReport'))];
      for (const [i, page] of pages.slice(1).entries()) {
        worker = worker.get('pdf').then(pdf => {
          pdf.addPage();
        }).from(page).toContainer().toCanvas().toPdf();
      }

      worker = worker.get('pdf').then(pdf => this.addHeaderAndFooter(pdf)).save().then(() => {
        this.renderer.setStyle(document.querySelector('body'), 'overflow', 'initial');
        document.querySelectorAll('#digitalReport').forEach(element => this.renderer.removeAttribute(element, 'class'));
        resolve();
      }).get('pdf').then(pdf => {
        // Escribir el base64 del pdf en el html para obtenerlo desde bowserprint
        document.querySelector('#base64').textContent = pdf.output('datauristring').toString().split('base64,')[1];
        document.querySelector('#base64').classList.add('base64');
      });

      setTimeout(() => {
        this.loadingService.stopLoading();
      }, 1000);
    }, 500));
  }

  private addHeaderAndFooter(pdf) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 2; i <= totalPages - 1; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(this.computedBodyStyle.getPropertyValue('--color-tertiary').trim());

      const relevateImg = new Image();
      relevateImg.src = 'assets/images/relevate-talent-assessment-logo.png';
      pdf.addImage(relevateImg, 'png', 15, 10, 55, 18);

      const randstadImg = new Image();
      randstadImg.src = 'assets/images/randstad-icon-pdf.png';
      pdf.addImage(randstadImg, 'png', 15, pdf.internal.pageSize.getHeight() - 19, 8, 4);

      pdf.text(
        (this.test.candidate.name
          ? this.utilsService.initials(this.test.candidate)
          : this.test.candidate.randstadCandidate) + ' | ' + (this.test.invitationId != 0 ? this.test.invitationId : this.test.externalId) + ' | | CD/01/SP', 25, pdf.internal.pageSize.getHeight() - 15);

      pdf.text((i).toString(), pdf.internal.pageSize.getWidth() - 15, pdf.internal.pageSize.getHeight() - 15);
    }
  }

}
